import IconButton from '@mui/material/IconButton';
import { Theme } from '@mui/material/styles';
import {
    ClearIcon,
    DateRange,
    DateRangePickerProps,
    DateRangePicker as MuiDateRangePicker,
    PickersShortcutsItem,
    PickerValidDate,
    SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import { useRef, useState } from 'react';
import { DateRangePickerCalendarHeader } from './DateRangePickerCalendarHeader';
import { DateRangePickerLayout } from './DateRangePickerLayout';

type CustomDateRangePickerProps = DateRangePickerProps<PickerValidDate> & {
    shortcuts?: PickersShortcutsItem<DateRange<Date>>[];
    singleInput?: boolean;
    label?: string;
    handleDateClear: () => void;
    enableYearSelection?: boolean;
};

const DateRangePicker = ({
    shortcuts,
    singleInput = false,
    label = 'Date',
    handleDateClear,
    enableYearSelection = false,
    ...props
}: CustomDateRangePickerProps) => {
    const [open, setOpen] = useState(false);
    const yearSelectOpenRef = useRef(false);

    return (
        <MuiDateRangePicker
            format={'yyyy-MM-dd'}
            {...props}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => {
                if (yearSelectOpenRef.current) return;
                setOpen(false);
                props.onClose?.();
            }}
            label={label}
            slots={{
                ...props.slots,
                field: singleInput ? SingleInputDateRangeField : undefined,
                layout: (props) => <DateRangePickerLayout {...props} shortcuts={shortcuts} />,
                calendarHeader: enableYearSelection
                    ? props.slots?.calendarHeader ?? DateRangePickerCalendarHeader
                    : undefined,
            }}
            slotProps={{
                ...props.slotProps,
                calendarHeader: {
                    ...props.slotProps?.calendarHeader,
                    onYearSelectOpen: () => {
                        yearSelectOpenRef.current = true;
                    },
                    onYearSelectClose: () => {
                        yearSelectOpenRef.current = false;
                    },
                    enableYearSelection,
                } as any,
                popper: {
                    ...props.slotProps?.popper,
                    modifiers: [
                        {
                            name: 'preventOverflow',
                            options: {
                                boundary: 'viewport',
                                tether: true,
                                altAxis: true,
                                padding: 16,
                            },
                        },
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 16],
                            },
                        },
                    ],
                },
                shortcuts: {
                    ...props.slotProps?.shortcuts,
                    items: shortcuts ?? [],
                },
                textField: ({ value }) => ({
                    fullWidth: true,
                    placeholder: 'Date',
                    InputProps: {
                        endAdornment:
                            value?.[0] && value?.[1] ? (
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDateClear();
                                    }}
                                    size="small"
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            ) : null,
                        classes: {
                            notchedOutline: 'shadow',
                        },
                    },
                    size: 'small',
                    InputLabelProps: {
                        sx: {
                            color: (theme: Theme) => theme.palette.black.main,
                        },
                    },
                    ...props.slotProps?.textField,
                    sx: {
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                        },
                        ...(props.slotProps?.textField as any)?.sx,
                    },
                }),
                layout: {
                    ...props.slotProps?.layout,
                    buttons: props.slotProps?.layout?.buttons ?? true,
                },
            }}
        />
    );
};

export { DateRangePicker };
